// src/views/examples/AdminEmployee.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardFooter,
  Table,
  Container,
  Row,
  Col,
  Button,
  Input,
  CardBody,
  Pagination,
  PaginationItem,
  PaginationLink,
  UncontrolledTooltip,
} from "reactstrap";

import Header from "components/Headers/Header";
import { useSessionStore } from "../../stores/sessionStore";
import { useTablePaging, PAGE_SIZE_OPTIONS } from "../../stores/pagingStore";
import { api } from "../../config/middleware";
import { API_GETEMPLOYEES, API_DELEMPLOYEE } from "../../config/api";
import Swal from "sweetalert2";
import LoadingSpinner from "components/LoadingSpinner";

const TABLE_KEY = "employees";

// ดึงข้อมูลจาก API แบบ server paging
async function fetchEmployees({ orgId }) {
  const res = await api.get(API_GETEMPLOYEES, {
    params: { ORGID: orgId },
  });
  const rows = res?.data?.data?.rows ?? [];
  const total = Number(res?.data?.data?.paging?.total ?? rows.length);
  return { rows, total };
}

const AdminEmployee = () => {
  const navigate = useNavigate();
  const orgId = useSessionStore((s) => s.profile?.orgId) ?? 0;

  const { page, pageSize, setPage, setPageSize } = useTablePaging(TABLE_KEY, 10);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [keyword, setKeyword] = useState("");

  // ✅ responsive: จอเล็กใช้ table เดิม / จอกลาง-ใหญ่ใช้ style แบบ Product
  const [isSmall, setIsSmall] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsSmall(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const pageIndex = Math.max(0, Number(page || 1) - 1);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / (pageSize || 10))),
    [total, pageSize]
  );

  useEffect(() => {
    if (!orgId) return;
    let alive = true;
    setLoading(true);
    setError("");

    fetchEmployees({ orgId })
      .then(({ rows, total }) => {
        if (!alive) return;
        setRows(Array.isArray(rows) ? rows : []);
        setTotal(Number(total || 0));
      })
      .catch((err) => {
        if (!alive) return;
        setRows([]);
        setTotal(0);
        setError(err?.message || "ไม่สามารถโหลดข้อมูลพนักงานได้");
      })
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [orgId, pageIndex, pageSize]);

  const gotoPage = (p) => {
    const next = Math.min(Math.max(1, p), totalPages);
    setPage(TABLE_KEY, next);
  };

  const renderFullname = (r) => {
    const first = r?.empFirstname || "";
    const last = r?.empLastname || "";
    const code = r?.empCode;
    const name = `${first} ${last}`.trim() || "-";
    return code ? `${name} (${code})` : name;
  };
  const renderBranch = (r) => r?.brNameTH || r?.brCode || "-";
  const renderPosition = (r) => r?.psName || "-";
  const renderMobile = (r) => r?.empMobile || "-";

  const handleEdit = (emp) => {
    navigate("/edit-employee", { state: { empId: emp?.empId, orgId } });
  };

  const handleDelete = async (emp) => {
    if (!emp?.empId) return;

    const confirm = await Swal.fire({
      icon: "warning",
      title: "ยืนยันลบพนักงาน?",
      html: `คุณต้องการลบพนักงาน <b>${renderFullname(emp)}</b> ใช่หรือไม่`,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
    });
    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);
      await api.delete(API_DELEMPLOYEE, {
        data: { ORGID: orgId, EMPID: emp.empId },
      });

      // reload data
      const { rows: newRows, total: newTotal } = await fetchEmployees({ orgId });
      setRows(newRows);
      setTotal(newTotal);

      await Swal.fire({
        icon: "success",
        title: "ลบสำเร็จ",
        text: `ลบพนักงาน ${renderFullname(emp)} เรียบร้อยแล้ว`,
        timer: 1500,
        showConfirmButton: false,
      });

      // ถ้าหน้าเดิมไม่มีรายการแล้ว และไม่ใช่หน้าแรก ให้ถอยหน้า
      if (rows.length === 1 && pageIndex > 0) {
        gotoPage(page - 1);
      }
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: err?.response?.data?.message || err?.message || "ลบไม่สำเร็จ",
      });
    } finally {
      setLoading(false);
    }
  };

  // ✅ กรองใน "หน้าปัจจุบัน" ด้วย keyword
  const displayRows = useMemo(() => {
    const k = keyword.trim().toLowerCase();
    if (!k) return rows;
    return rows.filter((r) => {
      const name = renderFullname(r).toLowerCase();
      const branch = renderBranch(r).toLowerCase();
      const mobile = (r.empMobile || "").toLowerCase();
      return name.includes(k) || branch.includes(k) || mobile.includes(k);
    });
  }, [rows, keyword]);

  const filteredTotal = total;

  const pageItems = useMemo(() => {
    const items = [];
    const maxAround = 2;
    const start = Math.max(1, page - maxAround);
    const end = Math.min(totalPages, page + maxAround);
    for (let p = start; p <= end; p++) items.push(p);
    return items;
  }, [page, totalPages]);

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col xs="12">
            <Card className="bg-secondary shadow">
              <CardHeader className="border-0 d-flex align-items-center justify-content-between">
                <h6 className="mb-0">ข้อมูลพนักงาน</h6>
                <Button color="success" onClick={() => navigate("/add-employee")}>
                  เพิ่มข้อมูลพนักงาน
                </Button>
              </CardHeader>

              <CardBody className="position-relative">
                {/* Toolbar */}
                <div className="mb-3">
                  <Row className="align-items-center">
                    <Col
                      xs="12"
                      md="6"
                      className="d-flex align-items-center"
                      style={{ gap: 8 }}
                    >
                      <Input
                        type="select"
                        value={pageSize}
                        onChange={(e) => setPageSize(TABLE_KEY, Number(e.target.value))}
                        style={{ width: 96 }}
                      >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Input>
                      <span className="text-muted">แถว/หน้า</span>
                    </Col>

                    <Col
                      xs="12"
                      md="6"
                      className="mt-2 mt-md-0 d-flex justify-content-md-end"
                    >
                      <Input
                        id="empSearch"
                        placeholder="ค้นหา ชื่อ-สกุลพนักงาน/สาขา/เบอร์โทร"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        style={{ maxWidth: 320, minWidth: 260 }}
                      />
                    </Col>
                  </Row>
                </div>

                {/* ✅ TABLE: เล็ก=เดิม, กลาง/ใหญ่=Product style */}
                {isSmall ? (
                  <div className="table-responsive">
                    <Table hover className="table table-modern mb-0">
                      <thead className="thead-colored">
                        <tr>
                          <th className="text-start">ลำดับ</th>
                          <th className="text-start">ชื่อ-สกุลพนักงาน</th>
                          <th className="text-start">ตำแหน่ง</th>
                          <th className="text-start">สาขา</th>
                          <th className="text-start">เบอร์โทร</th>
                          <th className="text-start">รายละเอียด</th>
                        </tr>
                      </thead>

                      <tbody>
                        {!!error && (
                          <tr>
                            <td colSpan={6} className="text-center text-danger py-4">
                              {error}
                            </td>
                          </tr>
                        )}

                        {!error && displayRows.length === 0 && (
                          <tr>
                            <td colSpan={6} className="text-center text-muted py-4">
                              ไม่พบข้อมูล
                            </td>
                          </tr>
                        )}

                        {!error &&
                          displayRows.map((r, idx) => (
                            <tr key={r.empId || `${idx}-${r.empUsername || ""}`}>
                              <td data-th="ลำดับ">{pageIndex * pageSize + idx + 1}</td>
                              <td
                                className="text-truncate"
                                title={renderFullname(r)}
                                data-th="ชื่อ-สกุลพนักงาน"
                              >
                                {renderFullname(r)}
                              </td>
                              <td
                                className="text-truncate"
                                title={renderPosition(r)}
                                data-th="ตำแหน่ง"
                              >
                                {renderPosition(r)}
                              </td>
                              <td
                                className="text-truncate"
                                title={renderBranch(r)}
                                data-th="สาขา"
                              >
                                {renderBranch(r)}
                              </td>
                              <td
                                className="text-truncate"
                                title={renderMobile(r)}
                                data-th="เบอร์โทร"
                              >
                                {renderMobile(r)}
                              </td>
                              <td className="text-start" data-th="รายละเอียด">
                                <div className="d-flex gap-2">
                                  <Button
                                    color="warning"
                                    size="sm"
                                    onClick={() => handleEdit(r)}
                                    id={`btnEdit-${r.empId}`}
                                  >
                                    แก้ไข
                                  </Button>
                                  <UncontrolledTooltip
                                    placement="top"
                                    target={`btnEdit-${r.empId}`}
                                  >
                                    ไปหน้าแก้ไขข้อมูลพนักงาน
                                  </UncontrolledTooltip>

                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => handleDelete(r)}
                                    id={`btnDelete-${r.empId}`}
                                  >
                                    ลบ
                                  </Button>
                                  <UncontrolledTooltip
                                    placement="top"
                                    target={`btnDelete-${r.empId}`}
                                  >
                                    ลบพนักงานรายการนี้
                                  </UncontrolledTooltip>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="zi-table-wrap">
                    <Table responsive hover className="zi-table mb-0">
                      <thead>
                        <tr>
                          <th className="text-start" style={{ width: 60 }}>
                            ลำดับ
                          </th>
                          <th className="text-start" style={{ width: 200 }}>
                            ชื่อ-สกุลพนักงาน
                          </th>
                          <th className="text-start" style={{ width: 160 }}>
                            ตำแหน่ง
                          </th>
                          <th className="text-start" style={{ width: 160 }}>
                            สาขา
                          </th>
                          <th className="text-start" style={{ width: 160 }}>
                            เบอร์โทร
                          </th>
                          <th className="text-end" style={{ width: 160 }}>
                            การจัดการ
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {!!error ? (
                          <tr>
                            <td colSpan={6} className="text-center text-danger py-4">
                              {error}
                            </td>
                          </tr>
                        ) : displayRows.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center text-muted py-4">
                              ไม่พบข้อมูล
                            </td>
                          </tr>
                        ) : (
                          displayRows.map((r, idx) => (
                            <tr key={r.empId || `${idx}-${r.empUsername || ""}`}>
                              <td className="text-start">{pageIndex * pageSize + idx + 1}</td>
                              <td className="text-start fw-600">{renderFullname(r)}</td>
                              <td className="text-start">{renderPosition(r)}</td>
                              <td className="text-start">{renderBranch(r)}</td>
                              <td className="text-start">{renderMobile(r)}</td>
                              <td className="text-end">
                                <div className="d-inline-flex gap-2">
                                  <Button
                                    color="warning"
                                    size="sm"
                                    onClick={() => handleEdit(r)}
                                    id={`btnEdit2-${r.empId}`}
                                  >
                                    แก้ไข
                                  </Button>
                                  <UncontrolledTooltip
                                    placement="top"
                                    target={`btnEdit2-${r.empId}`}
                                  >
                                    ไปหน้าแก้ไขข้อมูลพนักงาน
                                  </UncontrolledTooltip>

                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => handleDelete(r)}
                                    id={`btnDelete2-${r.empId}`}
                                  >
                                    ลบ
                                  </Button>
                                  <UncontrolledTooltip
                                    placement="top"
                                    target={`btnDelete2-${r.empId}`}
                                  >
                                    ลบพนักงานรายการนี้
                                  </UncontrolledTooltip>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* Summary */}
                <div className="d-flex justify-content-end justify-content-sm-end justify-content-center mt-2">
                  <div className="text-muted small">
                    แสดง {(page - 1) * pageSize + (displayRows.length ? 1 : 0)}–
                    {Math.min(page * pageSize, (page - 1) * pageSize + displayRows.length)} จากทั้งหมด{" "}
                    {filteredTotal} รายการ
                  </div>
                </div>

                {/* ✅ CSS แบบ Product */}
                <style>{`
                  .zi-table-wrap{
                    background:#fff;
                    border-radius:10px;
                    border:1px solid rgba(0,0,0,.06);
                    overflow:hidden;
                  }
                  .zi-table{ margin:0; }
                  .zi-table thead th{
                    background:#f7fafc;
                    color:#0f172a;
                    font-weight:700;
                    font-size:14px;
                    border-bottom:1px solid rgba(0,0,0,.06);
                    padding:14px 16px;
                    white-space:nowrap;
                  }
                  .zi-table tbody td{
                    padding:14px 16px;
                    border-top:1px solid rgba(0,0,0,.06);
                    vertical-align:middle;
                    font-size:14px;
                    color:#0f172a;
                  }
                  .zi-table tbody tr:hover{ background:#f9fbff; }
                  .fw-600{ font-weight:600; }
                `}</style>
              </CardBody>

              <CardFooter className="py-4">
                <nav aria-label="employee pagination">
                  <Pagination className="pagination justify-content-center mb-0">
                    <PaginationItem disabled={page <= 1}>
                      <PaginationLink onClick={() => gotoPage(page - 1)}>
                        <i className="fas fa-angle-left" />
                      </PaginationLink>
                    </PaginationItem>

                    {pageItems[0] > 1 && (
                      <>
                        <PaginationItem>
                          <PaginationLink onClick={() => gotoPage(1)}>1</PaginationLink>
                        </PaginationItem>
                        {pageItems[0] > 2 && <span className="px-2">…</span>}
                      </>
                    )}

                    {pageItems.map((p) => (
                      <PaginationItem key={p} active={p === page}>
                        <PaginationLink onClick={() => gotoPage(p)}>{p}</PaginationLink>
                      </PaginationItem>
                    ))}

                    {pageItems[pageItems.length - 1] < totalPages && (
                      <>
                        {pageItems[pageItems.length - 1] < totalPages - 1 && (
                          <span className="px-2">…</span>
                        )}
                        <PaginationItem>
                          <PaginationLink onClick={() => gotoPage(totalPages)}>
                            {totalPages}
                          </PaginationLink>
                        </PaginationItem>
                      </>
                    )}

                    <PaginationItem disabled={page >= totalPages}>
                      <PaginationLink onClick={() => gotoPage(page + 1)}>
                        <i className="fas fa-angle-right" />
                      </PaginationLink>
                    </PaginationItem>
                  </Pagination>
                </nav>
              </CardFooter>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AdminEmployee;
